import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Product, Store } from "../../../../types/ProductListing";
import ProductTableRow from "./ProductTableRow";
import { TablePagination } from "src/shared/components/table";
import "./ProductTable.css";

export type SortField =
  | "title"
  | "price"
  | "totalStock"
  | "salesQty30d"
  | "updatedAt";
export type SortDirection = "asc" | "desc";

export interface ProductTableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  className?: string;
  responsive?: "always" | "md" | "lg";
}

export interface ProductTableProps {
  products: Product[];
  stores?: Store[];
  loading?: boolean;
  onSortChange: (field: SortField, direction: SortDirection) => void;
  onRowClick?: (product: Product) => void;
  onStockUpdate?: (product: Product) => void;
  onPriceUpdate?: (product: Product) => void;
  onEditProduct?: (product: Product) => void;
  sortField?: SortField;
  sortDirection?: SortDirection;
  className?: string;
  emptyMessage?: string;
  showVariants?: boolean;
  // Pagination props
  pagination?: {
    currentPage: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    itemName?: string;
  };
}

const defaultColumns: ProductTableColumn[] = [
  {
    key: "product",
    label: "Product",
    sortable: true,
    className: "",
    responsive: "always",
  },
  {
    key: "masterProduct",
    label: "Master Product",
    className: "",
    responsive: "md",
  },
  { key: "store", label: "Store", className: "w-180px", responsive: "md" },
  {
    key: "stock",
    label: "Stock",
    sortable: true,
    className: "text-end",
    responsive: "md",
  },
  {
    key: "price",
    label: "Price",
    sortable: true,
    className: "text-end",
    responsive: "md",
  },
  {
    key: "sold30d",
    label: "Sold (30d)",
    sortable: true,
    className: "text-end",
    responsive: "lg",
  },
  { key: "sync", label: "Last Sync", className: "text-end", responsive: "lg" },
  {
    key: "quality",
    label: "Product Information Quality",
    className: "",
    responsive: "lg",
  },
  {
    key: "actions",
    label: "Actions",
    className: "w-150px",
    responsive: "always",
  },
];

function ProductTable({
  products,
  stores = [],
  loading = false,
  onSortChange,
  onRowClick,
  onStockUpdate,
  onPriceUpdate,
  onEditProduct,
  sortField,
  sortDirection = "asc",
  className = "",
  emptyMessage,
  showVariants = false,
  pagination,
}: ProductTableProps) {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const handleSort = (field: SortField) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";
    onSortChange(field, newDirection);
  };

  const toggleRowExpansion = (productId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedRows(newExpanded);
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return "material-symbols-outlined text-gray-400";
    return sortDirection === "asc"
      ? "material-symbols-outlined text-primary"
      : "material-symbols-outlined text-primary";
  };

  const getSortIconName = (field: SortField) => {
    if (sortField !== field) return "unfold_more";
    return sortDirection === "asc"
      ? "keyboard_arrow_up"
      : "keyboard_arrow_down";
  };

  // Removed getResponsiveClass - all columns should always be visible to match original design

  const formatPrice = (price: number, currency: string = "IDR") => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getStockStatusClass = (stock: number) => {
    if (stock === 0) return "text-red-600 font-bold";
    if (stock <= 10) return "text-amber-600 font-bold";
    return "text-gray-800";
  };

  const renderSkeletonRow = () => (
    <tr key={`skeleton-${Math.random()}`}>
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-flex tw-items-start tw-gap-3">
          <div className="tw-h-12 tw-w-12 tw-rounded tw-bg-gray-200 tw-animate-pulse"></div>
          <div className="tw-flex-1 tw-space-y-2">
            <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-3/4"></div>
            <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/2"></div>
            <div className="tw-flex tw-gap-2">
              <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-8"></div>
              <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-8"></div>
              <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-12"></div>
            </div>
          </div>
        </div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-24"></div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-space-y-1">
          <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-32"></div>
          <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-6"></div>
        </div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-right tw-align-top">
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-8"></div>
          <div className="tw-h-4 tw-w-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
        </div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-right tw-align-top">
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <div className="tw-space-y-1">
            <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-16"></div>
            <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-12"></div>
          </div>
          <div className="tw-h-4 tw-w-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
        </div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-right tw-align-top">
        <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-8"></div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-text-right tw-align-top">
        <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <div className="tw-h-4 tw-w-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
          <div className="tw-h-3 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-12"></div>
        </div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-h-6 tw-bg-gray-200 tw-rounded-full tw-animate-pulse tw-w-16"></div>
      </td>
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-grid tw-grid-cols-2 tw-gap-1">
          <div className="tw-h-12 tw-w-12 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
          <div className="tw-h-12 tw-w-12 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
          <div className="tw-h-12 tw-w-12 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
          <div className="tw-h-12 tw-w-12 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
        </div>
      </td>
    </tr>
  );

  const renderEmptyState = () => {
    return (
      <tr>
        <td className="tw-px-6 tw-py-24 tw-text-center" colSpan={9}>
          <div className="tw-mx-auto tw-flex tw-max-w-sm tw-flex-col tw-items-center tw-justify-center tw-text-center">
            <div className="tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-50">
              <span className="material-symbols-outlined tw-text-4xl tw-text-gray-400">
                search_off
              </span>
            </div>
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">
              {t("ProductListing.EmptyProductListing")}
            </h3>
            <p className="tw-mt-2 tw-text-sm tw-text-gray-500">
              Your search criteria didn't match any products. Try clearing
              filters or adding a new product.
            </p>
            <button className="tw-mt-6 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-primary/90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2">
              <span className="material-symbols-outlined tw-mr-2 tw-text-xl">
                add_circle
              </span>
              {t("ProductListing.Button.AddProduct")}
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className={clsx(
        "product-table-container tw-h-full tw-flex tw-flex-col",
        className
      )}
      data-testid="product-table-container"
    >
      {/* Table Area - matches HTML reference exactly */}
      <div className="tw-flex-1 tw-overflow-x-auto tw-overflow-y-auto">
        <div className="tw-inline-block tw-min-w-full tw-align-middle">
          <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white">
            <table className="tw-min-w-full tw-divide-y tw-divide-gray-200" data-testid="product-table">
              <thead className="tw-bg-gray-50" data-testid="product-table-header">
                <tr>
                  {/* Product column */}
                  <th
                    className="tw-w-[380px] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Product
                  </th>

                  {/* Merchant Product column */}
                  <th
                    className="tw-w-[150px] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Merchant Product
                  </th>

                  {/* Store column */}
                  <th
                    className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[180px]"
                    scope="col"
                  >
                    Store
                  </th>

                  {/* Stock column */}
                  <th
                    className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Stock
                  </th>

                  {/* Price column */}
                  <th
                    className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Price
                  </th>

                  {/* Sold (30d) column */}
                  <th
                    className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Sold (30d)
                  </th>

                  {/* Last Sync column */}
                  <th
                    className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Last Sync
                  </th>

                  {/* Product Information Quality column */}
                  <th
                    className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Product Information Quality
                  </th>

                  {/* Actions column */}
                  <th
                    className="tw-w-[150px] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                    scope="col"
                  >
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="tw-divide-y tw-divide-gray-200" data-testid="product-table-body">
                {loading
                  ? // Loading skeleton rows
                    Array.from({ length: 5 }, (_, index) => renderSkeletonRow())
                  : products.length === 0
                  ? // Empty state
                    renderEmptyState()
                  : // Product rows using ProductTableRow component
                    products.map((product) => (
                      <ProductTableRow
                        key={product.id}
                        product={product}
                        stores={stores}
                        onRowClick={onRowClick}
                        onStockUpdate={onStockUpdate}
                        onPriceUpdate={onPriceUpdate}
                        onEditProduct={onEditProduct}
                        showVariants={showVariants}
                      />
                    ))}
              </tbody>
            </table>

            {/* Embedded Pagination */}
            {pagination && (
              <TablePagination
                currentPage={pagination.currentPage}
                totalItems={pagination.totalItems}
                pageSize={pagination.pageSize}
                onPageChange={pagination.onPageChange}
                onPageSizeChange={pagination.onPageSizeChange}
                itemName={pagination.itemName || "products"}
                loading={loading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductTable;
